import React, { useState, useEffect } from 'react';

const scoreColor = s => s >= 4 ? '#2E7D32' : s >= 3 ? '#1565C0' : s >= 2 ? '#F57F17' : '#C62828';
const barColor   = s => s >= 4 ? '#4CAF50' : s >= 3 ? '#1565C0' : s >= 2 ? '#FFA000' : '#EF5350';
const pct = (n, d) => d > 0 ? Math.round((n / d) * 100) : 0;
const statusPill = s => {
  if (s === 'provided') return <span className="pill pill-green">Compliant</span>;
  if (s === 'partial')  return <span className="pill pill-amber">Partial</span>;
  if (s === 'missing')  return <span className="pill pill-red">Gap</span>;
  return <span className="pill pill-gray">Pending</span>;
};

const TIER_LABELS = { me5:'ME5', me5_copilot:'ME5+Copilot', me5_a365:'ME5+A365', me7:'ME7' };
const TIER_COLORS = { me5:'#455A64', me5_copilot:'#1565C0', me5_a365:'#0078D4', me7:'#6A1B9A' };
const TIER_DESC = {
  me5:'Base platform — Sentinel, Defender, Purview, Entra',
  me5_copilot:'AI assistant — Copilot Runtime, Studio, RAI guardrails',
  me5_a365:'Agentic layer — Agent registry, Tool Gateway, third-party discovery',
  me7:'Full stack — all capabilities unified',
};

const LENSES = [
  { id:'domain',    label:'Domain',    icon:'D' },
  { id:'layer',     label:'Layer',     icon:'L' },
  { id:'licensing', label:'Licensing', icon:'$' },
  { id:'status',    label:'Status',    icon:'!' },
  { id:'discovery', label:'Discovery', icon:'?' },
];

// ── Shared control detail card ───────────────────────────────────────────────
function ControlCard({ c }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{borderBottom:'1px solid #EEF0F3'}}>
      <div onClick={() => setOpen(!open)} style={{
        display:'grid', gridTemplateColumns:'70px 1fr 70px 80px 30px',
        gap:8, alignItems:'center', padding:'8px 12px', cursor:'pointer',
        background: open ? '#F8FAFC' : 'transparent',
      }}>
        <span style={{fontSize:11,fontWeight:600,color:'#1565C0'}}>{c.id}</span>
        <div style={{fontSize:11,color:'#455A64',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{c.statement}</div>
        <div style={{textAlign:'center',fontWeight:700,fontSize:13,color:c.score!=null?scoreColor(c.score):'#B0BAC5'}}>{c.score!=null?`${c.score}/5`:'—'}</div>
        <div>{statusPill(c.status)}</div>
        <div style={{fontSize:14,color:'#B0BAC5',textAlign:'center',transform:open?'rotate(90deg)':'none',transition:'transform 0.15s'}}>&#8250;</div>
      </div>
      {open && (
        <div style={{padding:'8px 12px 14px 82px',background:'#F8FAFC',fontSize:10,lineHeight:1.6}}>
          {c.objective && <div style={{color:'#7A8899',fontStyle:'italic',marginBottom:6}}>Objective: {c.objective}</div>}
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'4px 20px'}}>
            <div><span style={{color:'#7A8899'}}>Domain:</span> <span style={{fontWeight:500}}>{c.domain} — {c.domain_name}</span></div>
            <div><span style={{color:'#7A8899'}}>Layer:</span> <span style={{fontWeight:500}}>{c.agentic_layer}</span></div>
            <div><span style={{color:'#7A8899'}}>Source:</span> <span style={{fontWeight:500}}>{c.source}</span></div>
            <div><span style={{color:'#7A8899'}}>Licensing:</span> <span className="pill" style={{fontSize:8,background:(TIER_COLORS[c.licensing_tier]||'#455A64')+'18',color:TIER_COLORS[c.licensing_tier]||'#455A64'}}>{TIER_LABELS[c.licensing_tier]||c.licensing_tier}</span></div>
            {(c.atlas_techniques||[]).length > 0 && (
              <div style={{gridColumn:'1 / -1'}}>
                <span style={{color:'#7A8899'}}>ATLAS:</span>{' '}
                {c.atlas_techniques.map(t => (
                  <span key={t} style={{display:'inline-block',padding:'1px 5px',margin:'0 2px 2px 0',borderRadius:3,background:'#FCE4EC',color:'#C62828',fontSize:8,fontWeight:600,fontFamily:'monospace'}}>{t}</span>
                ))}
              </div>
            )}
          </div>
          {c.capability_required && <div style={{marginTop:6}}><span style={{color:'#1565C0',fontWeight:600}}>Capability:</span> {c.capability_required}</div>}
          {c.product_recommendation && <div><span style={{color:'#00796B',fontWeight:600}}>Product:</span> {c.product_recommendation}</div>}
          {c.policy_configuration && <div><span style={{color:'#E65100',fontWeight:600}}>Policy config:</span> {c.policy_configuration}</div>}
          {c.agent365_visibility && c.agent365_visibility !== 'Not directly visible.' && !c.agent365_visibility.startsWith('Not directly') && (
            <div style={{marginTop:4,padding:'4px 8px',background:'#E3F2FD',borderRadius:4,color:'#0078D4'}}>
              <span style={{fontWeight:600}}>A365 visibility:</span> {c.agent365_visibility}
            </div>
          )}
          {c.assessor_notes && <div style={{marginTop:4,color:'#546E7A',fontStyle:'italic'}}>Notes: {c.assessor_notes}</div>}
          {(c.sub_controls||[]).map(sc => (
            <div key={sc.id} style={{marginTop:6,padding:'6px 8px',background:'#F0F4F8',borderRadius:4,borderLeft:'2px solid #0078D4'}}>
              <span style={{fontWeight:600,color:'#0078D4'}}>{sc.id}</span> — {sc.statement}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Main Dashboard ───────────────────────────────────────────────────────────
export default function Dashboard({ API, domains, overview, engagement, refresh, allowedLenses }) {
  const allowed = allowedLenses || new Set(LENSES.map(l => l.id));
  const visibleLenses = LENSES.filter(l => allowed.has(l.id));
  const [lens, setLens] = useState(visibleLenses[0]?.id || 'domain');
  const [layers, setLayers] = useState([]);
  const [controls, setControls] = useState([]);
  const [discovery, setDiscovery] = useState(null);
  const [expandedGroup, setExpandedGroup] = useState(null);

  useEffect(() => {
    Promise.all([
      fetch(`${API}/api/agentic-layers`).then(r => r.json()),
      fetch(`${API}/api/controls`).then(r => r.json()),
    ]).then(([l, c]) => { setLayers(l); setControls(c); });
  }, [API]);

  useEffect(() => {
    if (lens === 'discovery' && !discovery) {
      fetch(`${API}/api/discovery`).then(r => r.json()).then(setDiscovery);
    }
  }, [lens, API, discovery]);

  if (!overview) return <div className="empty-state">Loading...</div>;

  const os = overview.overall_score || 0;
  const scored = overview.scored_controls || 0;
  const total = overview.total_controls || 55;
  const gaps = overview.gaps || 0;

  const toggle = (id) => setExpandedGroup(expandedGroup === id ? null : id);

  // ── Group controls by lens ──
  const groupedCards = () => {
    if (lens === 'domain') {
      return domains.map(d => ({
        id: d.id, title: `${d.id} — ${d.name}`, score: d.score, color: d.color,
        total: d.controls, scored: d.scored, gaps: d.status_counts?.missing || 0,
        controls: controls.filter(c => c.domain === d.id),
      }));
    }
    if (lens === 'layer') {
      return layers.map(l => ({
        id: l.id, title: `${l.order}. ${l.name}`, score: l.score, color: l.color,
        total: l.total, scored: l.scored, gaps: l.gaps, sub: l.m365,
        controls: controls.filter(c => c.agentic_layer === l.id),
      }));
    }
    if (lens === 'licensing') {
      return ['me5','me5_copilot','me5_a365','me7'].map(t => {
        const ctrls = controls.filter(c => c.licensing_tier === t);
        const scoredC = ctrls.filter(c => c.score != null);
        const avg = scoredC.length > 0 ? (scoredC.reduce((s,c) => s+c.score, 0) / scoredC.length) : 0;
        return {
          id: t, title: TIER_LABELS[t], score: Math.round(avg*10)/10, color: TIER_COLORS[t],
          total: ctrls.length, scored: scoredC.length, gaps: ctrls.filter(c => c.status === 'missing').length,
          sub: TIER_DESC[t], controls: ctrls,
        };
      });
    }
    if (lens === 'status') {
      return [
        { id:'missing', title:'Gaps (missing)', color:'#C62828', statusFilter:'missing' },
        { id:'partial', title:'Partial compliance', color:'#F57F17', statusFilter:'partial' },
        { id:'provided', title:'Compliant', color:'#2E7D32', statusFilter:'provided' },
        { id:'pending', title:'Pending (not scored)', color:'#455A64', statusFilter:'pending' },
      ].map(g => {
        const ctrls = controls.filter(c => c.status === g.statusFilter);
        const scoredC = ctrls.filter(c => c.score != null);
        const avg = scoredC.length > 0 ? (scoredC.reduce((s,c) => s+c.score, 0) / scoredC.length) : 0;
        return { ...g, score: Math.round(avg*10)/10, total: ctrls.length, scored: scoredC.length, gaps: g.statusFilter === 'missing' ? ctrls.length : 0, controls: ctrls };
      });
    }
    return [];
  };

  const cards = lens !== 'discovery' ? groupedCards() : [];

  return (
    <div>
      {/* Summary strip */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:12,marginBottom:18}}>
        <div className="stat-card">
          <div className="stat-val" style={{color:scoreColor(os)}}>{os}</div>
          <div className="stat-lbl">Overall maturity</div>
          <div className="stat-sub">{os >= 3.5 ? 'Managed' : os >= 2.5 ? 'Defined' : os >= 1.5 ? 'Developing' : 'Initial'} / 5.0</div>
        </div>
        <div className="stat-card">
          <div className="stat-val">{scored}/{total}</div>
          <div className="stat-lbl">Controls scored</div>
          <div className="stat-sub">{pct(scored,total)}% complete</div>
        </div>
        <div className="stat-card">
          <div className="stat-val" style={{color:gaps>15?'#C62828':'#F57F17'}}>{gaps}</div>
          <div className="stat-lbl">Gaps identified</div>
          <div className="stat-sub">{gaps > 20 ? 'Critical' : gaps > 10 ? 'High' : 'Moderate'}</div>
        </div>
        <div className="stat-card">
          <div className="stat-val" style={{color:'#0078D4'}}>{discovery ? discovery.summary.shadow_ai_count : '—'}</div>
          <div className="stat-lbl">Shadow AI apps</div>
          <div className="stat-sub">{discovery ? `${discovery.summary.shadow_ai_users} users` : 'Run discovery'}</div>
        </div>
      </div>

      {/* Lens toggle */}
      <div style={{display:'flex',gap:4,marginBottom:20,background:'#F5F7F9',borderRadius:10,padding:4}}>
        {visibleLenses.map(l => (
          <button key={l.id} onClick={() => setLens(l.id)} style={{
            flex:1, padding:'8px 0', fontSize:11, fontWeight:600, borderRadius:8,
            border:'none', cursor:'pointer', transition:'all 0.15s',
            background: lens === l.id ? '#fff' : 'transparent',
            color: lens === l.id ? '#1565C0' : '#7A8899',
            boxShadow: lens === l.id ? '0 1px 4px rgba(0,0,0,0.08)' : 'none',
          }}>
            {l.label}
          </button>
        ))}
      </div>

      {/* ═══ DOMAIN / LAYER / LICENSING / STATUS LENSES ═══ */}
      {lens !== 'discovery' && (
        <div>
          {cards.map(card => {
            const isOpen = expandedGroup === card.id;
            const w = pct(card.score || 0, 5);
            return (
              <div key={card.id} className="card" style={{marginBottom:8,overflow:'hidden'}}>
                <div onClick={() => toggle(card.id)} style={{
                  display:'grid', gridTemplateColumns:'minmax(200px,1fr) 120px 60px 70px 20px',
                  gap:12, alignItems:'center', padding:'14px 18px', cursor:'pointer',
                  background: isOpen ? '#F0F6FF' : '#fff',
                }}>
                  <div>
                    <div style={{fontSize:13,fontWeight:600,color:'#0D1B2A'}}>{card.title}</div>
                    {card.sub && <div style={{fontSize:10,color:'#7A8899',marginTop:1}}>{card.sub}</div>}
                  </div>
                  <div>
                    <div style={{height:6,borderRadius:3,background:'#EEF0F3',overflow:'hidden'}}>
                      <div style={{width:`${w}%`,height:'100%',borderRadius:3,background:barColor(card.score||0),transition:'width 0.4s'}}/>
                    </div>
                    <div style={{fontSize:9,color:'#7A8899',marginTop:2}}>{card.scored}/{card.total} scored</div>
                  </div>
                  <div style={{textAlign:'center',fontSize:16,fontWeight:700,color:scoreColor(card.score||0)}}>{card.score||'—'}</div>
                  <div style={{textAlign:'center'}}>
                    <span style={{fontSize:10,fontWeight:600,padding:'2px 8px',borderRadius:8,
                      background:card.gaps>0?'#FFEBEE':'#E8F5E9',color:card.gaps>0?'#C62828':'#2E7D32'}}>
                      {card.gaps} gap{card.gaps!==1?'s':''}
                    </span>
                  </div>
                  <div style={{fontSize:14,color:'#B0BAC5',transform:isOpen?'rotate(90deg)':'none',transition:'transform 0.15s'}}>&#8250;</div>
                </div>
                {isOpen && card.controls && (
                  <div style={{borderTop:'1px solid #EEF0F3'}}>
                    {card.controls.map(c => <ControlCard key={c.id} c={c}/>)}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ═══ DISCOVERY LENS ═══ */}
      {lens === 'discovery' && discovery && (
        <div>
          {/* Discovery summary */}
          <div style={{display:'grid',gridTemplateColumns:'repeat(5,1fr)',gap:10,marginBottom:20}}>
            {[
              {val:discovery.summary.sanctioned_count, label:'Sanctioned agents', color:'#2E7D32', bg:'#E8F5E9'},
              {val:discovery.summary.shadow_ai_count, label:'Shadow AI apps', color:'#C62828', bg:'#FFEBEE'},
              {val:discovery.summary.shadow_ai_users, label:'Shadow AI users', color:'#E65100', bg:'#FFF3E0'},
              {val:discovery.summary.unregistered_agents, label:'Unregistered agents', color:'#F57F17', bg:'#FFF8E1'},
              {val:discovery.summary.mcp_servers_registered, label:'MCP servers', color:'#455A64', bg:'#ECEFF1'},
            ].map((s,i) => (
              <div key={i} style={{padding:'14px',textAlign:'center',background:s.bg,borderRadius:10,border:`1px solid ${s.color}22`}}>
                <div style={{fontSize:24,fontWeight:700,color:s.color}}>{s.val}</div>
                <div style={{fontSize:10,color:'#455A64',marginTop:2}}>{s.label}</div>
              </div>
            ))}
          </div>

          {/* Shadow AI — the money section */}
          <div className="card" style={{marginBottom:12}}>
            <div className="card-header" style={{background:'#FFEBEE',borderBottom:'1px solid #FFCDD2'}}>
              <div>
                <div className="card-header-title" style={{color:'#C62828'}}>Shadow AI — unsanctioned tools ({discovery.shadow_ai.length})</div>
                <div className="card-header-sub">{discovery.summary.shadow_ai_users} users across {discovery.shadow_ai.length} tools — detected via Entra Internet Access, MCAS, browser signals</div>
              </div>
            </div>
            <div style={{padding:0}}>
              <table className="tbl" style={{fontSize:11}}>
                <thead>
                  <tr>
                    <th>App</th>
                    <th>Category</th>
                    <th style={{width:50}}>Users</th>
                    <th>Data risk</th>
                    <th>Detection</th>
                    <th style={{width:80}}>Controls</th>
                    <th>Recommendation</th>
                  </tr>
                </thead>
                <tbody>
                  {discovery.shadow_ai.map((app, i) => (
                    <tr key={i} style={{background: app.data_risk.startsWith('High') ? '#FFF8F8' : 'inherit'}}>
                      <td style={{fontWeight:600,color:'#0D1B2A'}}>{app.name}</td>
                      <td style={{color:'#7A8899'}}>{app.category}</td>
                      <td style={{textAlign:'center',fontWeight:600,color:app.users>10?'#C62828':'#F57F17'}}>{app.users}</td>
                      <td>
                        <span style={{fontSize:9,fontWeight:500,padding:'2px 6px',borderRadius:6,
                          background:app.data_risk.startsWith('High')?'#FFEBEE':app.data_risk.startsWith('Medium')?'#FFF8E1':'#E8F5E9',
                          color:app.data_risk.startsWith('High')?'#C62828':app.data_risk.startsWith('Medium')?'#F57F17':'#2E7D32'}}>
                          {app.data_risk.split(' — ')[0]}
                        </span>
                        <div style={{fontSize:9,color:'#7A8899',marginTop:2}}>{app.data_risk.split(' — ')[1]||''}</div>
                      </td>
                      <td style={{fontSize:10,color:'#546E7A'}}>{app.detection}</td>
                      <td style={{fontSize:9,color:'#1565C0'}}>{app.controls.join(', ')}</td>
                      <td style={{fontSize:10,color:'#455A64'}}>
                        {app.data_risk.startsWith('High') ? 'Block immediately' : app.users > 10 ? 'Review and approve or block' : 'Monitor'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Sanctioned agents */}
          <div className="card" style={{marginBottom:12}}>
            <div className="card-header" style={{background:'#E8F5E9',borderBottom:'1px solid #A5D6A7'}}>
              <div>
                <div className="card-header-title" style={{color:'#2E7D32'}}>Sanctioned agents ({discovery.sanctioned_agents.length})</div>
                <div className="card-header-sub">Registered in Entra Agent ID / A365 agent registry</div>
              </div>
            </div>
            <div style={{padding:0}}>
              <table className="tbl" style={{fontSize:11}}>
                <thead>
                  <tr>
                    <th>Agent</th>
                    <th>Type</th>
                    <th>Identity</th>
                    <th>Permissions</th>
                    <th>Data access</th>
                    <th>Owner</th>
                    <th style={{width:50}}>Risk</th>
                  </tr>
                </thead>
                <tbody>
                  {discovery.sanctioned_agents.map((a, i) => (
                    <tr key={i}>
                      <td style={{fontWeight:600,color:'#0D1B2A'}}>{a.name}</td>
                      <td style={{color:'#7A8899'}}>{a.type}</td>
                      <td style={{fontSize:10}}>{a.identity}</td>
                      <td style={{fontSize:10}}>{a.permissions}</td>
                      <td style={{fontSize:10,color:'#546E7A'}}>{a.data_access}</td>
                      <td style={{fontSize:10}}>{a.owner}</td>
                      <td><span style={{fontSize:9,fontWeight:500,padding:'2px 6px',borderRadius:6,
                        background:a.risk==='High'?'#FFEBEE':a.risk==='Medium'?'#FFF8E1':'#E8F5E9',
                        color:a.risk==='High'?'#C62828':a.risk==='Medium'?'#F57F17':'#2E7D32'}}>{a.risk}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Third-party agents + MCP */}
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:12}}>
            <div className="card">
              <div className="card-header" style={{background:'#FFF8E1',borderBottom:'1px solid #FFE082'}}>
                <div>
                  <div className="card-header-title" style={{color:'#F57F17'}}>Third-party agents ({discovery.third_party_agents.length})</div>
                  <div className="card-header-sub">Unregistered — need A365 governance</div>
                </div>
              </div>
              <div className="card-body">
                {discovery.third_party_agents.map((a, i) => (
                  <div key={i} style={{padding:'8px 0',borderBottom:i<discovery.third_party_agents.length-1?'1px solid #F0F2F5':'none'}}>
                    <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                      <span style={{fontSize:12,fontWeight:600,color:'#0D1B2A'}}>{a.name}</span>
                      <span style={{fontSize:9,padding:'2px 6px',borderRadius:6,background:a.risk==='High'?'#FFEBEE':'#FFF8E1',color:a.risk==='High'?'#C62828':'#F57F17'}}>{a.risk} risk</span>
                    </div>
                    <div style={{fontSize:10,color:'#7A8899',marginTop:2}}>
                      {a.connection} · {a.permissions} · MCP: {a.mcp_registered ? 'Registered' : 'Not registered'}
                    </div>
                    <div style={{fontSize:9,color:'#1565C0',marginTop:2}}>Controls: {a.controls.join(', ')}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="card">
              <div className="card-header" style={{background:'#ECEFF1',borderBottom:'1px solid #CFD8DC'}}>
                <div>
                  <div className="card-header-title" style={{color:'#455A64'}}>Agent Tool Gateway (MCP)</div>
                  <div className="card-header-sub">MCP server registration and tool connection policies</div>
                </div>
              </div>
              <div className="card-body">
                <div style={{textAlign:'center',padding:'20px 0'}}>
                  <div style={{fontSize:28,fontWeight:700,color:'#C62828'}}>0</div>
                  <div style={{fontSize:11,color:'#7A8899',marginTop:4}}>MCP servers registered</div>
                  <div style={{fontSize:10,color:'#455A64',marginTop:8,padding:'8px 12px',background:'#F8F9FA',borderRadius:8,display:'inline-block'}}>
                    No Agent Tool Gateway deployed. All agent tool connections are ungoverned.
                  </div>
                  <div style={{fontSize:9,color:'#1565C0',marginTop:8}}>
                    Controls: AI.4.6a · AI.4.6b — requires Agent 365
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Copilot extensions */}
          <div className="card" style={{marginBottom:12}}>
            <div className="card-header">
              <div>
                <div className="card-header-title">Copilot extensions & plugins ({discovery.copilot_extensions.length})</div>
                <div className="card-header-sub">Installed Copilot plugins, extensions, and custom connectors</div>
              </div>
            </div>
            <div className="card-body" style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:10}}>
              {discovery.copilot_extensions.map((ext, i) => (
                <div key={i} style={{padding:'10px 12px',background:'#F8F9FA',borderRadius:8,border:'1px solid #EEF0F3'}}>
                  <div style={{fontSize:12,fontWeight:600,color:'#0D1B2A'}}>{ext.name}</div>
                  <div style={{fontSize:10,color:'#7A8899',marginTop:2}}>{ext.publisher} · {ext.users} users</div>
                  <div style={{fontSize:10,color:'#546E7A',marginTop:2}}>Data: {ext.data_access}</div>
                  <span style={{fontSize:9,padding:'2px 6px',borderRadius:6,marginTop:4,display:'inline-block',
                    background:ext.status==='sanctioned'?'#E8F5E9':'#FFF8E1',
                    color:ext.status==='sanctioned'?'#2E7D32':'#F57F17'}}>{ext.status}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Controls impacted */}
          <div className="info-box info-box-blue">
            <strong>{discovery.summary.controls_impacted.length} controls impacted by discovery findings:</strong>{' '}
            {discovery.summary.controls_impacted.join(', ')}
          </div>
        </div>
      )}

      {lens === 'discovery' && !discovery && (
        <div className="empty-state">Loading discovery data...</div>
      )}
    </div>
  );
}
